import React, { useEffect, useState } from 'react';
import {
  SafeAreaView,
  ScrollView,
  StyleSheet,
  View,
  TouchableOpacity,
  Linking,
  Dimensions,
  Platform,
  Alert,
  Image,
} from 'react-native';
import Modal from 'react-native-modal';
import ReactDOMServer from 'react-dom/server';
import { Icon, Text } from 'react-native-elements';
import lodash, { isEmpty } from 'lodash';

const { width: windowWidth, height: windowHeight } = Dimensions.get('window');
const debug = require('debug')('se:advertoverlay');

import { getTopImageUrl } from '../../lib/advert';
import {
  trackAdvertAction,
  trackAdvertDetailView,
  trackThumbtackDetailView,
} from '../../lib/analytics';
import analytics from '../../lib/wrapAnalytics';
import Carousel, { DisplayType } from '../../components/Carousel';
import Divider from '../../components/Divider';
import ExploreImage from '../../components/ZTImage';
import ThumbtackItem from '../ThumbtackItem';

import {
  Advertisement,
  Image as AdvertImage,
  Action,
  ActionType,
  Tag,
  isThumbtackPro,
  ThumbtackPro,
  isAdvertisement,
} from '../../api/adserver';
import config from '../../lib/config';
import { openLocation, withHttp } from '../../lib/linking';

import AdvertContactList from './AdvertContactList';

export interface Props {
  item?: Advertisement | ThumbtackPro | null;
  itemContext?: any;
  onClose: () => void;
  onPressTag: (tag: Tag) => void;
}

interface AdvertAction {
  id: string;
  title: string;
  icon: string | null;
  action: Action;
}

const AdvertOverlay: React.FC<Props> = (props) => {
  const [visible, setVisible] = useState(true);
  const { item, itemContext } = props;

  const [advertItem, setAdvertItem] = useState<Advertisement | null>();
  const [thumbtackItem, setThumbtackItem] = useState<ThumbtackPro | null>();

  useEffect(() => {
    if (!item) {
      return;
    }
    if (isThumbtackPro(item)) {
      setThumbtackItem(item);
    } else {
      // is advertisement
      setAdvertItem(item);
    }
  }, [item]);

  useEffect(() => {
    analytics.trackScreen('AdvertOverlay');
    if (isAdvertisement(item)) {
      trackAdvertDetailView(item);
    } else if (isThumbtackPro(item)) {
      trackThumbtackDetailView(item, itemContext);
    }
  }, [item, itemContext]);

  const showClose = true;

  if (!item) {
    return null;
  }

  const onClose = () => {
    setVisible(false);
    const { onClose: handleOnClose } = props;
    handleOnClose && handleOnClose();
  };

  let topImageUrl = getTopImageUrl(item);

  let advertActions: AdvertAction[] = [];

  if (isAdvertisement(item)) {
    advertActions = item.actions.map<AdvertAction>((action) => {
      const icon = lodash.get(action, 'icon.image.url', null);
      return {
        id: action.id,
        title: action.title,
        icon,
        action,
      };
    });
  }
  const handleOnPressTag = (tag: Tag) => {
    debug(`tag: ${tag.id} pressed`);
    const { onPressTag } = props;
    if (onPressTag) {
      onPressTag(tag);
    }
  };

  const onReferalLinkPress = () => {
    if (thumbtackItem) {
      trackAdvertAction(
        'ThumbtackReferalAction',
        thumbtackItem.business_name,
        itemContext.category.title,
        thumbtackItem.id.toString(),
        itemContext.category.title,
      );
      Linking.openURL(thumbtackItem.thumbtack_url)
        .then(() => debug(`opened url:${thumbtackItem.thumbtack_url}`))
        .catch((e) => debug(`error opening url.  ${e.toString()}`));
    }
  };

  const onPressAction = (actionItem: AdvertAction) => {
    debug('onPressAction');
    const { action } = actionItem;
    if (isAdvertisement(item)) {
      trackAdvertAction(
        action.title,
        item.headline,
        item.categories[0].title,
        item.id.toString(),
        item.categories[0].id.toString(),
      );
    }
    switch (action.action_type) {
      case ActionType.Email: {
        const { url: emailAddress } = action;
        if (!emailAddress) {
          debug('email not set.  not linking');
          break;
        }
        Linking.openURL(`mailto:${emailAddress}`)
          .then(() => debug('opened email'))
          .catch((e) => debug(`error opening email.  ${e.toString()}`));
        break;
      }
      case ActionType.Web: {
        const { url: webUrl } = action;
        if (!webUrl) {
          debug('url not set.  not linking');
          break;
        }
        const linkUrl = withHttp(webUrl);
        Linking.openURL(linkUrl)
          .then(() => debug(`opened url:${linkUrl}`))
          .catch((e) => debug(`error opening url.  ${e.toString()}`));
        break;
      }
      case ActionType.Phone: {
        const { url: phoneNumber } = action;
        Alert.alert(phoneNumber);
        if (!phoneNumber) {
          debug('phoneNumber not set.  not linking');
          break;
        }
        Linking.openURL(`tel://${phoneNumber}`)
          .then(() =>
            debug(
              `(internal component) - opening call for number${phoneNumber}`,
            ),
          )
          .catch((e) => debug(`error opening call.  ${e.toString()}`));
        break;
      }
      case ActionType.Directions: {
        if (isAdvertisement(item)) {
          const { latitude, longitude } = item.closest_location;
          if (!latitude || !longitude) {
            debug('lat/lng not set.  not opening directions');
            break;
          }
          openLocation(latitude, longitude, item);
        }
        break;
      }
      default: {
        debug(
          `unknown onPressContact itemType: ${
            action.action_type
          }, action: ${JSON.stringify(action)}, advert.id: ${
            item.id
          }.  Doing nothing`,
        );
      }
    }
  };

  const hasGallery =
    isAdvertisement(item) &&
    item.carousel_images &&
    item.carousel_images.length > 0;

  let advertCaroselImages: AdvertImage[] = [];
  if (hasGallery && item.carousel_images) {
    advertCaroselImages = item.carousel_images;
  }
  const getHeadLine = () => {
    if (advertItem && advertItem.headline) {
      return advertItem.headline;
    }
  };

  const getBiLine = () => {
    if (advertItem && advertItem.bi_line) {
      return advertItem.bi_line;
    }
  };

  const tagCarousel = (items: any[]) => {
    return (
      <Carousel
        displayType={DisplayType.badges}
        items={items}
        onPressBadge={handleOnPressTag}
        name="adverttags"
      />
    );
  };

  const getTagCarousel = () => {
    if (advertItem && advertItem.tags) {
      return tagCarousel(advertItem.tags);
    }
  };

  const yearsInBusiness = () => {
    if (thumbtackItem && thumbtackItem.years_in_business) {
      return (
        <View>
          {moreInfoTitle()}
          <Text style={[styles.detailText, styles.bottomMargin1Em]}>
            In Business For {thumbtackItem.years_in_business}{' '}
            {thumbtackItem.years_in_business === 1 ? 'Year' : 'Years'}
          </Text>
        </View>
      );
    }
  };

  const featuredReview = () => {
    if (thumbtackItem && thumbtackItem.featured_review) {
      return (
        <View>
          <Text style={[styles.featureHeading, styles.subSectionHeading]}>
            Sample Review
          </Text>
          <Text style={[styles.detailText, styles.bottomMargin1Em]}>
            {thumbtackItem.featured_review}
          </Text>
        </View>
      );
    }
  };

  const quoteCost = () => {
    if (
      thumbtackItem &&
      (thumbtackItem.quote.starting_cost || thumbtackItem.quote.cost_unit)
    ) {
      return (
        <View>
          <Text style={[styles.featureHeading, styles.subSectionHeading]}>
            Pricing Info
          </Text>
          <Text style={[styles.detailText, styles.bottomMargin1Em]}>
            {thumbtackItem.quote.starting_cost
              ? `From $${thumbtackItem.quote.starting_cost / 100} `
              : ''}
            {thumbtackItem.quote.cost_unit
              ? `per ${thumbtackItem.quote.cost_unit}`
              : ''}
          </Text>
        </View>
      );
    }
  };

  const moreInfoTitle = () => (
    <Text style={[styles.featureHeading, styles.subSectionHeading]}>
      More Info
    </Text>
  );

  const getBody = () => {
    if (advertItem && advertItem.body) {
      return (
        <View>
          {moreInfoTitle()}
          <Text style={[styles.detailText, styles.subSectionBody]}>
            {advertItem.body}
          </Text>
        </View>
      );
    }
    if (
      thumbtackItem &&
      (thumbtackItem.years_in_business ||
        thumbtackItem.featured_review ||
        (thumbtackItem.quote.starting_cost && thumbtackItem.quote.cost_unit))
    ) {
      return (
        <View>
          {featuredReview()}
          {quoteCost()}
          {yearsInBusiness()}
        </View>
      );
    }
  };

  const ContactSection = (exploreAdvertItem: Advertisement) => {
    // Checks to see if the component has any children within it's view (i.e. contact information) and if it doesn't then it won't display "contact" or the component
    const contactList = <AdvertContactList contactData={exploreAdvertItem} />;
    const contactListElement = ReactDOMServer.renderToStaticMarkup(contactList);

    if (Platform.OS !== 'web') {
      const emptyViewString = '<RCTView></RCTView>';
      if (contactListElement === emptyViewString) {
        return null;
      }
    } else {
      const t = document.createElement('template');
      t.innerHTML = contactListElement;
      const childNode = t.content.childNodes[0];

      if (childNode.childNodes.length === 0) {
        return null;
      }
    }
    return (
      <View style={[styles.featureHeadingContainer, styles.sectionStart]}>
        <Text style={[styles.featureHeading, styles.subSectionHeading]}>
          Contact
        </Text>
        {contactList}
      </View>
    );
  };

  const maxWidth = 600;
  const calculatedWidth = Math.min(maxWidth, windowWidth);
  console.log(`calculatedWidth: ${calculatedWidth}`);

  const modalDimStyles = {
    ...Platform.select({
      web: {
        maxWidth: calculatedWidth,
        margin: 'auto',
        // marginBottom: 40,
      },
    }),
  };

  const modalStyle = StyleSheet.flatten([styles.overlay, modalDimStyles]);

  const mainImageDimStyles = {
    ...Platform.select({
      web: {
        width: calculatedWidth,
        height: calculatedWidth * config.theme.advert.imageHeightRatio,
      },
      default: {
        height: windowWidth * config.theme.advert.imageHeightRatio,
        width: windowWidth,
      },
    }),
  };

  const mainImageStyle = StyleSheet.flatten([
    styles.mainImage,
    mainImageDimStyles,
  ]);

  const thumbtackSectionDimStyles = {
    ...Platform.select({
      web: {
        width: calculatedWidth,
      },
      default: {
        width: windowWidth,
      },
    }),
  };

  const thumbtackSectionStyle = StyleSheet.flatten([
    styles.thumbtackSection,
    thumbtackSectionDimStyles,
  ]);

  return (
    <Modal
      backdropColor={'#000000'}
      backdropOpacity={0.5}
      isVisible={visible}
      hideModalContentWhileAnimating
      style={modalStyle}
      onBackdropPress={onClose}
    >
      {showClose && (
        <View style={styles.topOverlay}>
          <View style={styles.closeButtonContainer}>
            <TouchableOpacity onPress={onClose}>
              <Icon
                tvParallaxProperties={null}
                activeOpacity={0.5}
                name={'close'}
                size={30}
                color={'#505050'}
                style={styles.closeButtonImage}
              />
            </TouchableOpacity>
          </View>
        </View>
      )}
      <SafeAreaView style={styles.scrollViewContainer}>
        <ScrollView
          contentInsetAdjustmentBehavior="automatic"
          style={styles.scrollView}
        >
          {topImageUrl && (
            <>
              {thumbtackItem ? (
                <View style={thumbtackSectionStyle}>
                  <ThumbtackItem
                    uri={topImageUrl}
                    imageStyle={styles.thumbtackItem}
                    buisnessName={thumbtackItem.business_name}
                    stars={thumbtackItem?.rating}
                    numReviews={thumbtackItem?.num_reviews}
                  />
                </View>
              ) : (
                <View>
                  <ExploreImage uri={topImageUrl} style={mainImageStyle} />
                  <Divider />
                  <View
                    style={[
                      styles.featureHeadingContainer,
                      styles.sectionStart,
                    ]}
                  >
                    <Text style={styles.featureHeading}>{getHeadLine()}</Text>
                    <Text style={[styles.detailText, styles.bottomMargin1Em]}>
                      {getBiLine()}
                    </Text>
                    {getTagCarousel()}
                  </View>
                </View>
              )}
            </>
          )}
          <Divider />
          {thumbtackItem ? (
            <View style={styles.thumbtackPriceButton}>
              <TouchableOpacity onPress={onReferalLinkPress}>
                <Text style={styles.thumbtackPriceText}>Get a Price</Text>
              </TouchableOpacity>
            </View>
          ) : (
            <Carousel
              name="advertactions"
              displayType={DisplayType.mini}
              items={advertActions}
              onPressData={onPressAction}
              singleLine
            />
          )}
          <Divider />
          <View style={[styles.featureHeadingContainer, styles.sectionStart]}>
            {getBody()}
          </View>
          <Divider />
          {advertItem ? (
            ContactSection(advertItem)
          ) : (
            <View style={[styles.featureHeadingContainer, styles.sectionStart]}>
              <Text>
                <Text style={styles.boldText}>Hire a Pro</Text> is powered by{' '}
                <Image
                  style={styles.thumbtackIcon}
                  source={require('../../assets/thumbtack_icon_100.jpg')}
                />{' '}
                Thumbtack.com
              </Text>
            </View>
          )}
          {/* </View> */}
          {hasGallery && (
            <>
              <Divider />
              <View
                style={[
                  styles.featureHeadingContainer,
                  styles.sectionStart,
                  styles.screenEnd,
                ]}
              >
                <Text style={[styles.featureHeading, styles.subSectionHeading]}>
                  Gallery
                </Text>
                <Carousel
                  name="advertcarousel"
                  displayType={DisplayType.photo}
                  items={advertCaroselImages}
                />
              </View>
            </>
          )}
        </ScrollView>
      </SafeAreaView>
    </Modal>
  );
};

const thumbtackHeight = 100;
const thumbtackWidth = 100;

const styles = StyleSheet.create({
  overlay: {
    margin: 0,
    marginTop: 40,
  },
  closeButtonContainer: {
    height: '100%',
    zIndex: 1000,
    flex: 1,
    alignContent: 'center',
    alignItems: 'flex-start',
    justifyContent: 'center',
    marginStart: 10,
  },
  closeButtonImage: {
    //    width: 30,
    //    marginStart: 10,
  },
  topOverlay: {
    flex: 1,
    position: 'absolute',
    opacity: 0.5,
    borderTopStartRadius: 10,
    borderTopEndRadius: 10,
    overflow: 'hidden',
    right: 0,
    top: 0,
    width: '100%',
    height: 40,
    fontWeight: 'bold',
    zIndex: 1001,
    backgroundColor: '#ffffff',
    padding: 0,
    marginTop: 0,
  },
  scrollView: {
    backgroundColor: '#ffffff',
    marginHorizontal: 0,
    borderRadius: 10,
  },
  featureHeadingContainer: {
    marginBottom: 5,
    marginHorizontal: 10,
  },
  sectionStart: {
    marginTop: 20,
    marginBottom: 20,
  },
  featureHeading: {
    fontSize: config.theme.headingFontSize,
    fontWeight: config.theme.headingFontWeight,
    color: config.theme.headingTextColor,
    marginBottom: 10,
  },
  subSectionHeading: {
    fontSize: 18,
    color: config.theme.advert.subSectionHeadingTextColor,
    fontWeight: '700',
  },
  subSectionBody: {
    color: config.theme.advert.subSectionHeadingTextColor,
  },
  mainImage: {
    height: windowWidth * config.theme.advert.imageHeightRatio,
    width: windowWidth,
  },
  thumbtackItem: {
    height: thumbtackHeight,
    width: thumbtackWidth,
  },
  thumbtackSection: {
    padding: 20,
    marginTop: 40,
  },
  thumbtackPriceButton: {
    backgroundColor: 'white',
    borderColor: config.theme.brandColor,
    borderWidth: 1,
    textAlign: 'center',
    width: '80%',
    alignSelf: 'center',
    justifyContent: 'center',
    height: 30,
    margin: 10,
    borderRadius: 2,
  },
  thumbtackPriceText: {
    color: config.theme.brandColor,
    alignSelf: 'center',
  },
  detailText: {
    fontSize: 14,
  },
  boldText: {
    fontWeight: 'bold',
  },
  bottomMargin1Em: {
    marginBottom: 14,
  },
  screenEnd: {
    marginBottom: 20,
  },
  thumbtackIcon: { height: 15, width: 15 },
  scrollViewContainer: {
    ...Platform.select({
      web: {
        flex: 1,
      },
      default: {
        height: windowHeight,
      },
    }),
  },
});

export default AdvertOverlay;
